import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import {
  type AgentExecution,
  type AgentExecutionMemoryState,
} from '@mai/types';

import { apiClient } from '../queries/axios';
import { useWebsocketPubSub } from '../queries/websockets';

export interface AgentExecutionStep {
  stepType: string;
  task?: string;
  thought?: string;
  code?: string;
  observation?: string;
  plan?: string;
  facts?: string;
  system?: string;
  finalAnswer?: {
    type: 'final_answer';
    output: string;
    resourceIds?: string[];
  };
  content?: string;
  tool?: string;
  input?: unknown;
  output?: unknown;
  timestamp?: string;
  startedAt?: Date | string;
  endedAt?: Date | string;
  error?: string;
  summary?: string;
}

export interface UseAgentExecutionLiveTailResult {
  execution: AgentExecution | null;
  steps: AgentExecutionStep[];
  currentVersion: number;
  totalVersions: number;
  isLoading: boolean;
  error: Error | null;
  latestMemoryState: AgentExecutionMemoryState | null;
}

export function useAgentExecutionLiveTail(
  agentExecutionId: string,
): UseAgentExecutionLiveTailResult {
  const queryClient = useQueryClient();
  const websocket = useWebsocketPubSub();

  // Fetch live tail data (execution + latest memory state)
  const {
    data: liveTailData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['agentExecutionLiveTail', agentExecutionId],
    queryFn: async () => {
      const response = await apiClient.get(
        `/agent-executions/${agentExecutionId}/live-tail`,
      );
      return response.data;
    },
    enabled: !!agentExecutionId,
    staleTime: 1000, // Keep data fresh for 1 second
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: false, // Don't retry failed requests
    // Remove polling - WebSocket will handle updates
  });

  // Subscribe to WebSocket updates - single, efficient update handler
  useEffect(() => {
    if (!agentExecutionId) {
      return;
    }

    const subscriptionId = websocket.subscribe(
      {
        topic: 'resource_updated',
        options: {
          resourceType: 'agent_execution',
          resourceId: agentExecutionId,
        },
      },
      () => {
        // Single, efficient invalidation - let React Query handle the refetch
        void queryClient.invalidateQueries({
          queryKey: ['agentExecutionLiveTail', agentExecutionId],
        });
      },
    );

    return () => {
      websocket.unsubscribe(subscriptionId);
    };
  }, [agentExecutionId, queryClient, websocket]);

  return {
    execution: liveTailData?.execution || null,
    steps: liveTailData?.steps || [],
    currentVersion: liveTailData?.latestMemoryState?.version || 0,
    totalVersions: liveTailData?.totalVersions || 0,
    isLoading,
    error: error as Error | null,
    latestMemoryState: liveTailData?.latestMemoryState || null,
  };
}

// Hook to fetch all memory state versions (for history view)
export function useAgentExecutionMemoryStates(
  agentExecutionId: string,
  options?: {
    version?: number;
    latest?: boolean;
    enabled?: boolean;
  },
) {
  return useQuery({
    queryKey: ['agentExecutionMemoryStates', agentExecutionId, options],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.version)
        params.append('version', options.version.toString());
      if (options?.latest) params.append('latest', 'true');

      const response = await apiClient.get(
        `/agent-executions/${agentExecutionId}/memory-states?${params}`,
      );
      return response.data;
    },
    enabled: options?.enabled !== false,
  });
}

// Hook to fetch a specific memory state version
export function useAgentExecutionMemoryStateVersion(
  agentExecutionId: string,
  version: number,
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    queryKey: ['agentExecutionMemoryState', agentExecutionId, version],
    queryFn: async () => {
      const response = await apiClient.get(
        `/agent-executions/${agentExecutionId}/memory-states/${version}`,
      );
      return response.data;
    },
    enabled: options?.enabled !== false && version > 0,
  });
}
